import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  LegendComponent,
  DataZoomComponent,
} from 'echarts/components';
import { LineSeriesOption, EChartsOption } from 'echarts/index';

import { CanvasRenderer } from 'echarts/renderers';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  CanvasRenderer,
  LegendComponent,
  DataZoomComponent,
  LineChart,
]);

class ChartUtils {
  static GRADIENT_FROM_OPACITY = '99';
  static GRADIENT_TO = '#ffffffe6';

  public static getYBoundaries(series: LineSeriesOption[]): {
    min: number;
    max: number;
    avg: number;
  } {
    let chartMin = Number.MAX_SAFE_INTEGER;
    let chartMax = 0;
    let chartAvg = 0;

    series.forEach(serie => {
      const serieData = serie.data as number[];
      const serieMin = Math.floor(Math.min(...serieData));
      const serieMax = Math.ceil(Math.max(...serieData));
      const average = serieData.reduce((a, b) => a + b, 0) / serieData.length;
      chartAvg += average;

      if (serieMin < chartMin) {
        chartMin = serieMin;
      }
      if (serieMax > chartMax) {
        chartMax = serieMax;
      }
    });

    chartAvg = Math.ceil(chartAvg / series.length);

    return { min: chartMin, max: chartMax, avg: chartAvg };
  }

  public static getSeriesGradient = (
    color: string,
    name: string,
    data: number[]
  ) => ({
    type: 'line',
    name,
    itemStyle: {
      color,
    },
    z: 2,
    areaStyle: {
      opacity: 0.8,
      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        {
          offset: 0,
          color: `${color}${ChartUtils.GRADIENT_FROM_OPACITY}`,
        },
        {
          offset: 1,
          color: ChartUtils.GRADIENT_TO,
        },
      ]),
    },
    data,
    smooth: true,
  });

  public static getSeriesLine = (
    color: string,
    name: string,
    data: number[],
    options: {
      symbolSize?: number;
      lineWidth?: number;
      fontWeight?: string;
    }
  ): LineSeriesOption => ({
    type: 'line',
    name,
    symbolSize: options.symbolSize || 7,
    itemStyle: {
      color,
    },
    lineStyle: {
      width: options.lineWidth || 2,
    },
    label: {
      fontWeight: options.fontWeight || ('normal' as any),
    },
    data,
    smooth: true,
  });

  public static SCALE_OPTIONS: EChartsOption = {
    xAxis: {
      type: 'category',
      boundaryGap: false,
      axisLabel: {
        align: 'center',
      },
      axisTick: 'inside',
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        showMinLabel: false,
      },
    },
  };

  public static BASE_OPTIONS: EChartsOption = {
    sampling: 'average',
    fontFamily: 'Inter, sans-serif',
    animationDuration: 300,
    backgroundColor: '#fff',
    legend: {
      itemGap: 10,
      top: 0,
      left: '5%',
      icon: 'circle',
      itemHeight: 6,
      itemWidth: 6,
      width: '100%',
      textStyle: {
        color: '#687A8B',
        fontWeight: 400,
        fontSize: 12,
      },
    },
    textStyle: {
      color: '#687A8B',
      fontWeight: 400,
      fontSize: 16,
    },
    tooltip: {
      trigger: 'axis',
      transitionDuration: 0.2,
      padding: 10,
      borderRadius: 8,
      shadowOffsetX: 0,
      shadowOffsetY: 3,
      shadowBlur: 5,
      textStyle: {
        fontSize: 16,
        fontWeight: 400,
      },
      formatter: function (params) {
        let tooltipFormat = `<div class="graph-tooltip__title">${params[0].name}</div>`;

        const parsedParams = params
          .map(param => {
            const bunnyTooltip =
              param.seriesName === 'bunny.net' ? ' graph-tooltip--bunny' : '';
            const symbol = `<span style="display:inline-block;border-radius:10px;width:10px;height:10px;background-color:${param.color};"></span>`;
            return `<div class="graph-tooltip${bunnyTooltip}">${
              symbol
            }<div class="graph-tooltip__content"><span class="graph-tooltip__name">${
              param.seriesName
            }</span><span class="graph-tooltip__value">${
              param.value
            }ms</span></div></div>`;
          })
          .join('');
        return `${tooltipFormat}${parsedParams}`;
      },
      valueFormatter: value => `${value}ms`,
    },
  };

  public static CHART_OPTIONS: EChartsOption = {
    grid: {
      borderColor: '#E6E9EC',
      left: 5,
      right: 5,
      bottom: 12,
      containLabel: true,
    },
    dataZoom: [
      {
        type: 'inside',
        disabled: true,
      },
    ],
    media: [
      {
        query: {
          minWidth: 768,
        },
        option: {
          grid: {
            left: '2%',
            right: '2%',
            top: '15%',
          },
          legend: {
            top: '5%',
            right: '2%',
            textStyle: {
              color: '#687A8B',
              fontWeight: 400,
              fontSize: 17,
            },
          },
        },
      },
    ],
  };
}
export const GraphType = {
  Manual: 'manual_graph_items',
  Perfops: 'perfops_graph_items',
} as const;

export default ChartUtils;
